import { AnalysisContentWidgetModel } from './AnalysisContentWidgetModel';
import AnalysisContentObjectModel from './AnalysisContentObjectModel';
import {
  IWidgetCombinedData,
  IWidgetData,
  IWidgetStaticData,
  nodeCombinedValues,
  nodeStaticValues,
  nodeValues,
} from '../common/services/http/analysisContentService';

export class AnalysisContentWidgetDataModel {
  widget: AnalysisContentWidgetModel;
  measures: number[];
  includeDescendants: boolean;
  nodes: any[];
  groups: any[];
  parentNode: any;
  investorId: number;
  staticProperties: any[];

  constructor(widget: any, measures: any, investorId: number, includeDescendants?: boolean, staticProperties?: any[]) {
    this.widget = widget;
    this.measures = measures;
    this.nodes = [];
    this.groups = [];
    this.includeDescendants = includeDescendants ? includeDescendants : false;
    this.investorId = investorId;
    this.staticProperties = staticProperties;
  }

  setInvestorId(investorId) {
    this.investorId = investorId;
  }

  defineNodesForParent(focusedObject, nodeId) {
    this.nodes.splice(0);
    this.groups.splice(0);
    this.parentNode = null;

    if (!focusedObject) return;
    this.parentNode = focusedObject.findNode(nodeId);
    if (this.parentNode) {
      this.nodes = focusedObject.childNodesBypassGroups(nodeId);
    }
  }

  _removeExcluded(focusedObject, nodesToCheck) {
    let excludedNodes = this.widget.nodesExcludedForObject(focusedObject.objectPath);

    for (let i = 0; i < excludedNodes.nodes.length; i++) {
      let exnode = excludedNodes.nodes[i];

      for (let j = 0; j < nodesToCheck.length; j++) {
        let nodes = nodesToCheck[j];
        let remove = -1;
        nodes.find(function(item, index) {
          let isEq = item.id === exnode.nodeId;
          if (isEq) remove = index;
          return isEq;
        });
        if (remove > -1) nodes.splice(remove, 1);
      }
    }
  }

  // Define Nodes for Parent
  defineNodes(focusedObject: AnalysisContentObjectModel): any {
    this.nodes.splice(0);
    this.groups.splice(0);

    if (this.widget.setup.sliceGroup) {
      if (this.widget.setup.sliceGroupLevel) {
        this.defineNodesForCategoryTypeLevel(focusedObject, this.widget.setup.sliceGroup, this.widget.setup.sliceGroupLevel);
      } else {
        this.defineNodesForGroup(focusedObject);
      }
    } else if (this.widget.setup.sliceType) {
      this.defineNodesForSliceType(focusedObject);
    } else {
      this.nodes = focusedObject.nodesOfObject.slice();
      this._removeExcluded(focusedObject, [this.nodes]);
    }
  }

  defineNodesForGroup(focusedObject: AnalysisContentObjectModel): any {
    this.groups = focusedObject.nodesFor(this.widget.setup.sliceGroup);
    this._removeExcluded(focusedObject, [this.groups]);

    for (let i = 0; i < this.groups.length; i++) {
      let group = this.groups[i];
      this.nodes.push.apply(this.nodes, focusedObject.childNodes(group.id));
    }
    return this.nodes;
  }

  defineNodesForSliceType(focusedObject) {
    this.groups.splice(0);
    this.nodes = focusedObject.nodesForType(this.widget.setup.sliceType);
    this._removeExcluded(focusedObject, [this.nodes]);
  }

  defineNodesForCategoryTypeLevel(focusedObject, categoryTypeId, categoryLevel) {
    this.nodes.splice(0);
    this.groups.splice(0);
    this.parentNode = null;

    this.nodes = focusedObject.nodesForCategoryTypeLevel(categoryTypeId, categoryLevel);

    let exclude = this.widget.nodesExcludedForObject(focusedObject.objectPath);

    for (let i = this.nodes.length - 1; i > -1; i--) {
      let parent = this.nodes[i];
      while (parent) {
        if (exclude.idLookup[parent.id]) {
          this.nodes.splice(i, 1);
          break;
        }
        parent = parent.parentNode;
      }
    }
  }

  fetchData(focusedPeriod, focusedCurrency): Promise<IWidgetData[]> {
    if (this.widget.setup.sliceGroup) {
      return Promise.resolve(
        nodeValues(
          +this.widget.contentId,
          this.nodes,
          this.groups,
          this.measures,
          focusedPeriod.endDate,
          this.widget.setup.singlePeriod,
          this.includeDescendants,
          focusedCurrency,
          this.investorId,
        ),
      );
    }
    return Promise.resolve(
      nodeValues(
        +this.widget.contentId,
        this.nodes,
        null,
        this.measures,
        focusedPeriod.endDate,
        this.widget.setup.singlePeriod,
        this.includeDescendants,
        focusedCurrency,
        this.investorId,
      ),
    );
  }

  fetchNodeData(focusedPeriod, focusedCurrency): Promise<IWidgetData[]> {
    return Promise.resolve(
      nodeValues(
        +this.widget.contentId,
        this.nodes,
        null,
        this.measures,
        focusedPeriod.endDate,
        this.widget.setup.singlePeriod,
        this.includeDescendants,
        focusedCurrency,
        this.investorId,
      ),
    );
  }

  fetchStaticData(): Promise<Array<IWidgetStaticData>> {
    if (this.widget.setup.sliceGroup) return nodeStaticValues(this.widget.contentId, null, this.groups, this.staticProperties, this.investorId);

    return nodeStaticValues(this.widget.contentId, this.nodes, null, this.staticProperties, this.investorId);
  }

  generateCurrencies = (scenarios) => {
    let currencies = [];

    this.nodes.forEach((node) => {
      let scenario = scenarios.find((item) => {
        return item.id === node.scenarioId;
      });
      if (scenario) {
        scenario.currencies.forEach((currency) => {
          if (!currencies.find((item) => {
            return item.currency === currency.currency;
          })) {
            currencies.push(currency);
          }
        });
      }
    });

    if (this.widget.setup.suppressLocalCurrency) {
      return currencies.filter((curr) => {
        return curr.currency !== '_';
      })
    }
    else{
      return currencies;
    }
  };

  fetchCombinedData(focusedPeriod, focusedCurrency): Promise<IWidgetCombinedData> {
    if (!focusedPeriod) return Promise.resolve<IWidgetCombinedData>(null);

    if (this.widget.setup.sliceGroup)
      return nodeCombinedValues(
        this.widget.contentId,
        null,
        this.groups,
        this.measures,
        this.staticProperties,
        focusedPeriod.endDate,
        this.widget.setup.singlePeriod,
        this.includeDescendants,
        focusedCurrency,
        this.investorId,
      );

    return nodeCombinedValues(
      this.widget.contentId,
      this.nodes,
      null,
      this.measures,
      this.staticProperties,
      focusedPeriod.endDate,
      this.widget.setup.singlePeriod,
      this.includeDescendants,
      focusedCurrency,
      this.investorId,
    );
  }
}
